import {
  createContext,
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import type { SessionViewerResult } from '../auth/api';

const NavigationScope = createContext<string | null>(null);

/** Une préférence de rubrique, jamais une session ou une autorisation. Le shell
 * est recréé à chaque changement d'identité, d'organisation ou de rôle réel. */
export function NavigationPreferences({
  viewer,
  children,
}: {
  readonly viewer: SessionViewerResult;
  readonly children: ReactNode;
}): React.JSX.Element {
  const scope = JSON.stringify([
    viewer.userId,
    viewer.accountKind,
    viewer.organization?.id ?? null,
    viewer.organization?.role ?? null,
  ]);
  return (
    <NavigationScope.Provider key={scope} value={scope}>
      {children}
    </NavigationScope.Provider>
  );
}

/** sessionStorage isole les onglets. Seuls des noms de rubriques autorisés sont
 * persistés : aucune saisie, ressource métier, fenêtre, clé ou donnée de session. */
export function useRememberedSection<T extends string>(
  name: string,
  fallback: T,
  allowed: readonly T[],
  preferFallback = false,
): readonly [T, Dispatch<SetStateAction<T>>] {
  const scope = useContext(NavigationScope);
  const key = scope === null ? null : `tosho.navigation.v1:${scope}:${name}`;
  const [section, setSection] = useState<T>(() => {
    if (key && !preferFallback) {
      try {
        const saved = window.sessionStorage.getItem(key);
        if (saved && allowed.includes(saved as T)) return saved as T;
      } catch {
        // Stockage refusé : la navigation reste utilisable pendant la session.
      }
    }
    return fallback;
  });
  useEffect(() => {
    if (!key) return;
    try {
      window.sessionStorage.setItem(key, section);
    } catch {
      // Une préférence ne doit jamais bloquer la page ou l'authentification.
    }
  }, [key, section]);
  return [section, setSection];
}
